import * as React from 'react';
import { useCulinaryCollectiveContent } from '@/hooks/cdaasHooks/cdaasHelperHooks';
import {
  Modal,
  ModalBody,
  ModalHeader,
  IconButton,
} from '@americanexpress/dls-react';
import { IconExpand } from '@americanexpress/dls-icons';
import { useModal } from '@/context/modalContext';
import { cn, getBasePathFromLocation } from '@/helpers/utils';
import { useRouter } from '@americanexpress/one-app-router';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useSelector } from 'react-redux';
import YouTubeVideo from '../common/YouTubeVideo';
import PageNavigation from '../common/PageNavigation';
import FAQs from './FAQs';
import Hero from './Hero';
import { CulinaryContent } from './interface';
import Partners from './partners';
import Divider from '../common/Divider';
import styles from './CulinaryCollectivePage.module.scss';

const CulinaryCollectivePage: React.FC<{ renderInModal?: boolean }> = ({
  renderInModal = false,
}) => {
  const router = useRouter();
  const { locale } = useSelector(getLocaleData);

  const [showExpandText, setShowExpandText] = React.useState(true);

  const navigateToCulinaryCollectivePage = () => {
    const basePath = getBasePathFromLocation(locale);
    router.push(`${basePath}/centurion-network/culinary-collective`);
  };

  React.useEffect(() => {
    if (!renderInModal) {
      return undefined;
    }

    setShowExpandText(true);
    const hideTextTimeout = window.setTimeout(() => {
      setShowExpandText(false);
    }, 1000);

    return () => {
      window.clearTimeout(hideTextTimeout);
    };
  }, [renderInModal]);

  const { closeModal } = useModal() as {
    closeModal: (
      event:
        | React.MouseEvent<HTMLElement>
        | MouseEvent
        | TouchEvent
        | KeyboardEvent,
      detail: string
    ) => void;
  };
  const culinaryContent: CulinaryContent = useCulinaryCollectiveContent();
  const {
    chefProfiles,
    heroImage,
    heroImageAltText,
    intro,
    profiles,
    sectionHeaders,
    expandAction,
    title,
    overviewVideoUrl,
    overviewVideoTitle,
  } = culinaryContent;

  const menus = [
    // Chef Partners Menu Section Items
    {
      menuSectionHeader: sectionHeaders.chefPartners,
      items: chefProfiles.map((profile) => ({
        ...profile,
        type: 'chef' as const,
      })),
    },
    ...profiles.map((profile) => ({
      menuSectionHeader: profile.header,
      items: [
        {
          ...profile,
          type: 'partner' as const,
        },
      ],
    })),
    {
      menuSectionHeader: sectionHeaders.faq,
      items: culinaryContent.faqs.map((faq, idx) => ({
        ...faq,
        type: 'faq' as const,
        idx,
        section: sectionHeaders.faq,
      })),
    },
  ];

  const renderPageContent = () => {
    return (
      <>
        <div
          className={cn('tw-mx-auto tw-max-w-screen-x2l', {
            'md_tw-px-0 md_tw-px-6 lg_tw-px-8 xl_tw-px-10': !renderInModal,
            [styles.modalHeroNoRadius]: renderInModal,
          })}
        >
          <Hero heroImage={heroImage} heroImageAltText={heroImageAltText} />
        </div>
        <div
          className={cn(
            'flex flex-align-items-center tw-mx-auto tw-max-w-screen-x2l tw-flex-col tw-px-4',
            {
              'md_tw-px-6 lg_tw-px-8 xl_tw-px-10': !renderInModal,
            }
          )}
        >
          <section
            className={cn('tw-my-10 tw-grid tw-grid-cols-1', {
              'md_tw-my-16 lg_tw-grid-cols-12': !renderInModal,
            })}
          >
            <div className="tw-col-span-2">
              <PageNavigation
                menus={menus}
                scrollContainerId={
                  renderInModal
                    ? 'culinary-collective-page-modal-content'
                    : undefined
                }
                showMobileView={renderInModal}
              />
            </div>
            <div
              className={cn('tw-col-span-8 tw-grid tw-grid-cols-1', {
                'lg_tw-grid-cols-8': !renderInModal,
              })}
            >
              <div
                className={cn(
                  'tw-col-span-6 tw-col-start-2 tw-mb-10 tw-font-sans tw-text-xl',
                  {
                    'md_tw-mb-16 lg_tw-text-2xl': !renderInModal,
                  }
                )}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
              <div className="tw-col-span-8">
                <YouTubeVideo
                  videoId={overviewVideoUrl}
                  title={overviewVideoTitle}
                />
                <div
                  className={cn({
                    'md_tw-grid md_tw-grid-cols-8': !renderInModal,
                  })}
                >
                  <Divider />
                </div>
              </div>

              <Partners
                partners={chefProfiles}
                sectionHeader={sectionHeaders.chefPartners}
                type="chef"
                showMobileView={renderInModal}
              />

              <Partners
                partners={profiles}
                type="partner"
                showMobileView={renderInModal}
              />

              <div className="tw-col-span-8">
                <FAQs
                  faqs={culinaryContent.faqs}
                  sectionHeader={sectionHeaders.faq}
                  showMobileView={renderInModal}
                />
              </div>
            </div>
          </section>
        </div>
      </>
    );
  };

  if (renderInModal) {
    return (
      <Modal
        id="culinary-collective-page-modal"
        aria-labelledby="culinary-collective-page-modal-header"
        onClose={(e) => closeModal(e, 'close culinary collective modal')}
        className="first-child_tw-items-end first-child_sm_tw-items-center"
        innerContainerClassName="tw-max-w-screen-sm md_tw-max-w-screen-md tw-m-0 sm_tw-m-4"
      >
        <ModalHeader id="culinary-collective-page-modal-header">
          <h3>{title}</h3>
        </ModalHeader>
        <div className="position-relative tw-overflow-hidden">
          <ModalBody
            id="culinary-collective-page-modal-content"
            className="tw-min-h-75dvh tw-p-0 tw-max-h-75vh-important sm_tw-min-h-full"
          >
            {renderPageContent()}
          </ModalBody>
          <IconButton
            screenReaderLabel="Expand to full page"
            variant="secondary"
            className={cn(
              'position-absolute tw-bottom-4 tw-right-4 tw-z-10 tw-transition-all tw-duration-300 tw-ease-in-out',
              showExpandText && 'tw-px-4'
            )}
            onClick={(e) => {
              closeModal(e, 'Navigate to culinary collective page from modal');
              navigateToCulinaryCollectivePage();
            }}
          >
            <div className="tw-flex tw-items-center">
              <div>
                <IconExpand size="md" color="brand" />
              </div>
              <span
                className={cn(
                  'display-inline-block tw-overflow-hidden tw-whitespace-nowrap tw-transition-all tw-duration-300 tw-ease-in-out',
                  showExpandText
                    ? 'tw-max-w-40 tw-pl-2 tw-opacity-100'
                    : 'tw-max-h-0 tw-max-w-0 tw-pl-0 tw-opacity-0'
                )}
                aria-hidden={!showExpandText}
              >
                {expandAction}
              </span>
            </div>
          </IconButton>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <div className="tw-mx-auto tw-my-10 tw-grid tw-max-w-screen-x2l tw-grid-cols-1 tw-px-4 md_tw-my-16 md_tw-px-0 lg_tw-grid-cols-12">
        <h1 className="text-align-center color-text-emphasis tw-heading-xl-book tw-col-span-8 tw-col-start-3 tw-font-serif md_tw-heading-4xl-book md_tw-font-serif">
          {title}
        </h1>
      </div>
      {renderPageContent()}
    </>
  );
};

export default CulinaryCollectivePage;
